import ky from "ky";

const TOKEN_KEY = "dfa_access_token";
const USER_KEY = "dfa_user";

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: "super_admin" | "owner" | "agent" | "scan_station" | "klien";
  tenant_id: string;
  franchisor_id: string | null;
  scope_type: "dcppi" | "franchisor";
}

export const auth = {
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },
  getUser(): AuthUser | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      return null;
    }
  },
  setSession(token: string, user: AuthUser): void {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  clear(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

// prefixUrl must end with "/" per ky docs. Without it, URLs like "api/foo"
// resolve relative to document.baseURI (i.e. /klien/X/api/foo when user is
// at /klien/X), causing 404. With "/" the same call becomes "/api/foo"
// → Vite dev proxy forwards to the API on :4000.
export const api = ky.create({
  prefixUrl: import.meta.env.VITE_API_BASE ?? "/",
  timeout: 30_000,
  retry: 0,
  hooks: {
    beforeRequest: [
      (request) => {
        const token = auth.getToken();
        if (token) request.headers.set("Authorization", `Bearer ${token}`);
      },
    ],
    afterResponse: [
      (request, _options, response) => {
        if (response.status === 401 && !request.url.includes("/auth/login")) {
          auth.clear();
          window.location.assign("/login");
        }
      },
    ],
  },
});

export async function apiGet<T>(url: string): Promise<T> {
  return api.get(url).json<T>();
}
export async function apiPost<T>(url: string, body?: unknown): Promise<T> {
  return api.post(url, body !== undefined ? { json: body } : undefined).json<T>();
}
export async function apiPatch<T>(url: string, body?: unknown): Promise<T> {
  return api.patch(url, body !== undefined ? { json: body } : undefined).json<T>();
}
export async function apiDelete<T>(url: string): Promise<T> {
  return api.delete(url).json<T>();
}
